/* The site header and footer (Round 1 design system), shared since Round 3 (R6/R7): the homepage, /later and the
   inner pages (/later/start, /later/gift, /teams) all render the same lp-nav2 header — logo, text links, Sign in, the
   page's primary button, a phone menu — and the same lp-foot footer. SiteShell wraps an inner page in both. */
import { useState, type ReactNode } from "react";
import { HOME, TAGLINE } from "@/data/homepage";
import { IcoClapper, IcoClose, IcoMenu } from "./icons";
import { lastPage, pageLabel } from "@/data/lastPage";

export type FootLink = { label: string; href: string };
export const FOOT_LINKS: FootLink[] = [
  { label: "Celebrations", href: "/" }, { label: "Occasions", href: "/occasions" }, ...(HOME.laterLink ? [{ label: "Keepsakes for later", href: "/later" }, { label: "Give as a gift", href: "/gift" }] : []), ...(HOME.comparePages ? [{ label: "Compare", href: "/compare/vidday" }] : []), ...(HOME.teamsLink ? [{ label: "For teams", href: "/teams" }] : []),
  { label: "About", href: "/about" }, { label: "Help center", href: "/help" }, { label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" },
  { label: "contact@keepsakepage.com", href: "mailto:contact@keepsakepage.com" },
];

/* `links` and `cta` are render functions given `close` so a tap in the phone menu can shut it before acting.
   Sign in (→ /app) sits after the text links and before the primary button, and repeats in the phone menu (R7). */
export function SiteHeader({ tag, links, cta, ctaMobile }: { tag?: string; links: (close: () => void) => ReactNode; cta: (close: () => void) => ReactNode; ctaMobile?: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  // 2026-10-25: an organizer who has opened a dashboard on this device gets the way straight back instead of "Sign in"
  const [lp] = useState(lastPage);
  const back = lp ? <a href={"/" + lp.slug + "#admin"} className="lp-continue" title="Back to your dashboard">Continue: {pageLabel(lp.name)} →</a> : <a href="/app">Sign in</a>;
  return (
    <div className="lp-nav lp-nav2">
      <a className="lp-logo" href="/" aria-label="KeepSakePage home"><IcoClapper /> KeepSakePage{tag && <span className="ow-logo-tag">{tag}</span>}</a>
      <nav className="lp-nav-links" aria-label="Site">{links(close)}{back}{cta(close)}</nav>
      <div className="lp-nav-m">{(ctaMobile || cta)(close)}<button className="lp-menu-btn" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? <IcoClose /> : <IcoMenu />}</button></div>
      {open && <div className="lp-menu" role="menu">{lp && back}{links(close)}<a href="/app">{lp ? "My pages" : "Sign in"}</a></div>}
    </div>);
}

/* `operator`: the company line, homepage only (2026-10-23, Twilio brand vetting for CourtNight needs the legal entity on the
   brand website's home page); every other page keeps the brand-only footer. */
export function SiteFooter({ links = FOOT_LINKS, operator }: { links?: FootLink[]; operator?: ReactNode }) {
  return (
    <footer className="lp-foot">
      <div className="lp-foot-brand"><IcoClapper size={18} /> KeepSakePage</div>
      <div>{TAGLINE}</div>
      <nav className="lp-foot-links">{links.map((l) => <a key={l.href} href={l.href}>{l.label}</a>)}</nav>
      {operator && <div className="lp-foot-legal lp-foot-op">{operator}</div>}
      <div className="lp-foot-legal">© {new Date().getFullYear()} KeepSakePage. All rights reserved.</div>
    </footer>);
}

/* An inner page: header, a centred column, footer. `narrow` is the 600px form column (/later/start, /later/gift). */
export function SiteShell({ tag, links, cta, ctaMobile, narrow, children }: { tag?: string; links: (close: () => void) => ReactNode; cta: (close: () => void) => ReactNode; ctaMobile?: (close: () => void) => ReactNode; narrow?: boolean; children: ReactNode }) {
  return (
    <div className="lp">
      <SiteHeader tag={tag} links={links} cta={cta} ctaMobile={ctaMobile} />
      <main className={"lp-inner" + (narrow ? " lp-inner-narrow" : "")}>{children}</main>
      <SiteFooter />
    </div>);
}
